import { useCallback, useEffect, useRef, useState } from 'react';
import { improve, isAbortError, type FlaggedFact } from '@/services/composeService';
import { IMPROVE_ERROR_TEXT, errorText } from './messages';

/** „Verbessern“ becomes available from this many words on. */
export const MIN_IMPROVE_WORDS = 3;

export function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

export type ImproveStatus = 'idle' | 'streaming' | 'improved';

type Options = {
  contactKey: string | null | undefined;
  /** Writes into the composer; called for every streamed piece and on restore. */
  setText: (text: string) => void;
  /** Test seam. */
  improveImpl?: typeof improve;
};

/**
 * Rewrites the draft through `POST /live-chat/compose/improve` and streams the
 * result into the composer. The original draft is kept for „Rückgängig“ and
 * restored on cancel and on any failure — the employee never loses text.
 */
export function useImproveText({ contactKey, setText, improveImpl = improve }: Options) {
  const [status, setStatus] = useState<ImproveStatus>('idle');
  const [flagged, setFlagged] = useState<FlaggedFact[]>([]);
  const [error, setError] = useState<string | null>(null);
  const originalRef = useRef<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const setTextRef = useRef(setText);
  setTextRef.current = setText;

  const start = useCallback(
    async (draft: string) => {
      if (!contactKey || controllerRef.current || countWords(draft) < MIN_IMPROVE_WORDS) return;
      const controller = new AbortController();
      controllerRef.current = controller;
      originalRef.current = draft;
      setError(null);
      setFlagged([]);
      setStatus('streaming');
      let streamed = '';
      try {
        const result = await improveImpl(
          contactKey,
          draft.trim(),
          (delta) => {
            if (controller.signal.aborted) return;
            streamed += delta;
            setTextRef.current(streamed);
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setTextRef.current(result.text);
        setFlagged(result.flagged);
        setStatus('improved');
      } catch (err) {
        // cancel() and unmount already cleaned up behind an abort.
        if (controller.signal.aborted || isAbortError(err)) return;
        setTextRef.current(originalRef.current ?? draft);
        originalRef.current = null;
        setStatus('idle');
        setError(errorText(err, IMPROVE_ERROR_TEXT));
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    [contactKey, improveImpl],
  );

  /** Stop a running rewrite and put the draft back. */
  const cancel = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller) return;
    controllerRef.current = null;
    controller.abort();
    if (originalRef.current !== null) setTextRef.current(originalRef.current);
    originalRef.current = null;
    setFlagged([]);
    setStatus('idle');
  }, []);

  /** „Rückgängig“: back to the draft as it was before „Verbessern“. */
  const undo = useCallback(() => {
    if (originalRef.current === null) return;
    setTextRef.current(originalRef.current);
    originalRef.current = null;
    setFlagged([]);
    setStatus('idle');
  }, []);

  /** Forget the improved state (after sending) without touching the text. */
  const reset = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    originalRef.current = null;
    setFlagged([]);
    setError(null);
    setStatus('idle');
  }, []);

  const dismissError = useCallback(() => setError(null), []);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  return { status, flagged, error, start, cancel, undo, reset, dismissError };
}
